import classNames from 'clsx';
import copy from 'copy-to-clipboard';
import { message, Tooltip } from 'aui';
import React, { useCallback, useRef, useState } from 'react';
import { formatMessageWithLocale } from '../../../../locales';
import type { MdComponentIndexProps } from '../type';
import styles from './MarkdownCopyToClipboard.module.less';
import { MarkdownSafeMdxViewerLocaleContext } from '../../../../components/MarkdownSafeMdxViewer/MarkdownSafeMdxViewerLocaleContext';

export interface MarkdownCopyToClipboardProp {
  text?: string;
}
export function MarkdownCopyToClipboard({
  text,
  children,
  ...props
}: React.PropsWithChildren<MarkdownCopyToClipboardProp & MdComponentIndexProps>) {
  const locale = React.useContext(MarkdownSafeMdxViewerLocaleContext);

  const ref = useRef<HTMLSpanElement>(null);
  const parentRef = useRef<HTMLSpanElement>(null);
  const [tooltip, setTooltip] = useState(formatMessageWithLocale(locale, { id: 'copyToClipboard' }));
  return (
    <Tooltip
      {...{
        overlayClassName: styles.tooltip,
        showArrow: false,
        title: tooltip,
        getPopupContainer: () => parentRef.current ?? document.body,
        getTooltipContainer: () => parentRef.current ?? document.body,
        onOpenChange: (open) => {
          if (!open) {
            setTimeout(() => {
              setTooltip(formatMessageWithLocale(locale, { id: 'copyToClipboard' }));
            }, 200);
          }
        },
      }}
    >
      <span ref={parentRef} data-index={props['data-first']} data-last={props['data-last']}>
        <span
          className={classNames('markdown-child-root', styles.text)}
          ref={ref}
          onClick={useCallback(
            (e) => {
              e.stopPropagation();
              const copyText = text || ref.current?.textContent || '';
              const result = copy(copyText);
              let msg;
              if (result) {
                const successTooltip = formatMessageWithLocale(locale, { id: 'overView.isCopied' });
                msg = successTooltip;
                message.success(msg);
              } else {
                const errorTooltip = formatMessageWithLocale(locale, { id: 'overView.failToCopied' });
                msg = errorTooltip;
                message.error(msg);
              }
              setTooltip(msg);
            },
            [text],
          )}
        >
          {children}
        </span>
      </span>
    </Tooltip>
  );
}

MarkdownCopyToClipboard.attributes = ['text'] as string[];
MarkdownCopyToClipboard.displayName = 'MarkdownCopyToClipboard';
MarkdownCopyToClipboard.inline = true;
